import { APP_ROLE } from '@/config/env'
import { ADMIN_ACCESS } from '@/entities/admin/lib/adminAccessCodes'

const homeByRole = {
  client: () => import('../views/client/home_view.vue'),
  courier: () => import('../views/courier/home_view.vue'),
  restaurant: () => import('../views/restaurant/home_view.vue'),
  admin: () => import('../views/admin/home_view.vue'),
}

export function buildRoutesForRole(role) {
  const home = homeByRole[role]
  if (!home) {
    throw new Error(`[buildRoutes] Unknown role: ${role}`)
  }
  if (role === 'courier') {
    return [
      {
        path: '/',
        component: home,
        children: [
          {
            path: '',
            name: 'courier-home',
            meta: { requiresAuth: true, titleKey: 'seo.pages.home' },
            component: () => import('../views/courier/home_screen.vue'),
          },
          {
            path: 'orders',
            name: 'courier-orders',
            meta: { requiresAuth: true, titleKey: 'seo.pages.orders' },
            component: () => import('../views/courier/orders_screen.vue'),
          },
          {
            path: 'map',
            name: 'courier-map',
            meta: { requiresAuth: true, titleKey: 'seo.pages.map' },
            component: () => import('../views/courier/map_screen.vue'),
          },
          {
            path: 'finance',
            name: 'courier-finance',
            meta: { requiresAuth: true, titleKey: 'seo.pages.finance' },
            component: () => import('../views/courier/finance_screen.vue'),
          },
          {
            path: 'login',
            name: 'courier-login',
            meta: { titleKey: 'seo.pages.login' },
            component: () => import('../views/courier/login_screen.vue'),
          },
          {
            path: 'register',
            name: 'courier-register',
            meta: { titleKey: 'seo.pages.register' },
            component: () => import('../views/courier/register_screen.vue'),
          },
          {
            path: 'profile',
            name: 'courier-profile',
            meta: { requiresAuth: true, titleKey: 'seo.pages.profile' },
            component: () => import('../views/courier/profile_screen.vue'),
          },
        ],
      },
    ]
  }
  if (role === 'client') {
    return [
      {
        path: '/',
        component: home,
        children: [
          {
            path: '',
            name: 'client-home',
            meta: { titleKey: 'seo.pages.home' },
            component: () => import('../views/client/home_screen.vue'),
          },
          {
            path: 'cuisine/:slug',
            name: 'client-cuisine',
            meta: { titleKey: 'seo.pages.cuisine' },
            component: () => import('../views/client/cuisine_screen.vue'),
          },
          {
            path: 'search',
            name: 'client-search',
            meta: { titleKey: 'seo.pages.search' },
            component: () => import('../views/client/search_screen.vue'),
          },
          {
            path: 'restaurants',
            name: 'client-restaurants',
            meta: { titleKey: 'seo.pages.restaurants' },
            component: () => import('../views/client/restaurants_screen.vue'),
          },
          {
            path: 'products',
            name: 'client-products',
            meta: { titleKey: 'seo.pages.products' },
            component: () => import('../views/client/products_screen.vue'),
          },
          {
            path: 'dish/:id',
            name: 'client-dish',
            meta: { fullBleed: true, titleKey: 'seo.pages.dish' },
            component: () => import('../views/client/dish_view.vue'),
          },
          {
            path: 'restaurant/:id',
            name: 'client-restaurant',
            meta: { titleKey: 'seo.pages.restaurant' },
            component: () => import('../views/client/restaurant_view.vue'),
          },
          {
            path: 'order/:id',
            name: 'client-order',
            meta: {
              hideClientCartSticky: true,
              requiresAuth: true,
              titleKey: 'seo.pages.order',
            },
            component: () => import('../views/client/order_view.vue'),
          },
          {
            path: 'orders',
            name: 'client-orders',
            meta: {
              hideClientCartSticky: true,
              requiresAuth: true,
              titleKey: 'seo.pages.orders',
            },
            component: () => import('../views/client/orders_screen.vue'),
          },
          {
            path: 'cart',
            name: 'client-cart',
            meta: {
              clientStickyCart: 'place-order',
              titleKey: 'seo.pages.cart',
            },
            component: () => import('../views/client/cart_view.vue'),
          },
          {
            path: 'payment',
            name: 'client-payment',
            meta: {
              hideClientCartSticky: true,
              requiresAuth: true,
              titleKey: 'seo.pages.payment',
            },
            component: () => import('../views/client/payment_screen.vue'),
          },
          {
            path: 'order-success',
            name: 'client-order-success',
            meta: {
              hideClientCartSticky: true,
              requiresAuth: true,
              titleKey: 'seo.pages.orderSuccess',
            },
            component: () => import('../views/client/order_success_screen.vue'),
          },
          {
            path: 'menu',
            redirect: { name: 'client-home' },
          },
          {
            path: 'login',
            name: 'client-login',
            meta: { hideClientCartSticky: true, titleKey: 'seo.pages.login' },
            component: () => import('../views/client/login_screen.vue'),
          },
          {
            path: 'register',
            name: 'client-register',
            meta: { hideClientCartSticky: true, titleKey: 'seo.pages.register' },
            component: () => import('../views/client/register_screen.vue'),
          },
          {
            path: 'profile',
            name: 'client-profile',
            meta: {
              requiresAuth: true,
              hideClientCartSticky: true,
              titleKey: 'seo.pages.profile',
            },
            component: () => import('../views/client/profile_screen.vue'),
          },
          {
            path: 'address',
            name: 'client-address',
            meta: { hideClientCartSticky: true, titleKey: 'seo.pages.address' },
            component: () => import('../views/client/address_screen.vue'),
          },
          {
            path: 'faq',
            name: 'client-faq',
            meta: { hideClientCartSticky: true, titleKey: 'seo.pages.faq' },
            component: () => import('../views/client/faq_screen.vue'),
          },
          {
            path: 'faq/:slug',
            name: 'client-faq-page',
            meta: { hideClientCartSticky: true },
            component: () => import('../views/client/cms_page_screen.vue'),
          },
          {
            path: 'invite',
            name: 'client-invite',
            meta: {
              hideClientCartSticky: true,
              requiresAuth: true,
              titleKey: 'seo.pages.invite',
            },
            component: () => import('../views/client/invite_screen.vue'),
          },
          {
            path: 'subscribe',
            name: 'client-subscribe',
            meta: { titleKey: 'seo.pages.subscribe' },
            component: () => import('../views/client/subscribe_screen.vue'),
          },
          {
            path: 'address/map',
            redirect: { name: 'client-address' },
          },
          {
            path: ':pathMatch(.*)*',
            redirect: { name: 'client-home' },
          },
        ],
      },
    ]
  }
  if (role === 'admin') {
    return [
      {
        path: '/',
        component: home,
        children: [
          {
            path: '',
            name: 'admin-orders',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.orders',
              accessCodes: [ADMIN_ACCESS.ORDERS, ADMIN_ACCESS.ORDERS_DELIVERY_FEE],
            },
            component: () => import('../views/admin/orders_screen.vue'),
          },
          {
            path: 'home',
            name: 'admin-home',
            redirect: { name: 'admin-orders' },
          },
          {
            path: 'orders',
            redirect: { name: 'admin-orders' },
          },
          {
            path: 'order/:id',
            name: 'admin-order-detail',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.order',
              accessCodes: [ADMIN_ACCESS.ORDERS, ADMIN_ACCESS.ORDERS_DELIVERY_FEE],
            },
            component: () => import('../views/admin/order_detail_screen.vue'),
          },
          {
            path: 'products',
            name: 'admin-products',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.products',
              accessCodes: [ADMIN_ACCESS.CATALOG, ADMIN_ACCESS.CATALOG_EDIT],
            },
            component: () => import('../views/admin/products_screen.vue'),
          },
          {
            path: 'product/:id',
            name: 'admin-product-detail',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.dish',
              accessCodes: [ADMIN_ACCESS.CATALOG, ADMIN_ACCESS.CATALOG_EDIT],
            },
            component: () => import('../views/admin/product_detail_screen.vue'),
          },
          {
            path: 'catalog',
            name: 'admin-catalog',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.catalog',
              accessCodes: [ADMIN_ACCESS.CATALOG_EDIT],
            },
            component: () => import('../views/admin/catalog_screen.vue'),
          },
          {
            path: 'restaurants',
            name: 'admin-restaurants',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.restaurants',
              accessCodes: [ADMIN_ACCESS.RESTAURANTS, ADMIN_ACCESS.RESTAURANTS_EDIT],
            },
            component: () => import('../views/admin/restaurants_screen.vue'),
          },
          {
            path: 'restaurant',
            name: 'admin-restaurant',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.restaurant',
              accessCodes: [ADMIN_ACCESS.RESTAURANTS_EDIT],
            },
            component: () => import('../views/admin/restaurant_screen.vue'),
          },
          {
            path: 'restaurant/:id',
            name: 'admin-restaurant-detail',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.restaurant',
              accessCodes: [ADMIN_ACCESS.RESTAURANTS, ADMIN_ACCESS.RESTAURANTS_EDIT],
            },
            component: () => import('../views/admin/restaurant_detail_screen.vue'),
          },
          {
            path: 'users',
            name: 'admin-users',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.users',
              accessCodes: [ADMIN_ACCESS.USERS],
            },
            component: () => import('../views/admin/users_screen.vue'),
          },
          {
            path: 'user/:id',
            name: 'admin-user-detail',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.user',
              accessCodes: [ADMIN_ACCESS.USERS],
            },
            component: () => import('../views/admin/user_detail_screen.vue'),
          },
          {
            path: 'wallets',
            name: 'admin-wallets',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.wallets',
              accessCodes: [ADMIN_ACCESS.LEDGER],
            },
            component: () => import('../views/admin/wallets_screen.vue'),
          },
          {
            path: 'wallet/:id',
            name: 'admin-wallet-detail',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.wallet',
              accessCodes: [ADMIN_ACCESS.LEDGER],
            },
            component: () => import('../views/admin/wallet_detail_screen.vue'),
          },
          {
            path: 'accounts',
            name: 'admin-accounts',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.accounts',
              accessCodes: [ADMIN_ACCESS.LEDGER],
            },
            component: () => import('../views/admin/accounts_screen.vue'),
          },
          {
            path: 'account/:id',
            name: 'admin-account-detail',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.account',
              accessCodes: [ADMIN_ACCESS.LEDGER],
            },
            component: () => import('../views/admin/account_detail_screen.vue'),
          },
          {
            path: 'transactions',
            name: 'admin-transactions',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.transactions',
              accessCodes: [ADMIN_ACCESS.LEDGER],
            },
            component: () => import('../views/admin/transactions_screen.vue'),
          },
          {
            path: 'operations',
            name: 'admin-operations',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.operations',
              accessCodes: [ADMIN_ACCESS.LEDGER],
            },
            component: () => import('../views/admin/operations_screen.vue'),
          },
          {
            path: 'operation/:id',
            name: 'admin-operation-detail',
            meta: {
              requiresAuth: true,
              titleKey: 'seo.pages.operation',
              accessCodes: [ADMIN_ACCESS.LEDGER],
            },
            component: () => import('../views/admin/operation_detail_screen.vue'),
          },
          {
            path: 'login',
            name: 'admin-login',
            meta: { titleKey: 'seo.pages.login' },
            component: () => import('../views/admin/login_screen.vue'),
          },
          {
            path: 'profile',
            name: 'admin-profile',
            meta: { requiresAuth: true, titleKey: 'seo.pages.profile' },
            component: () => import('../views/admin/profile_screen.vue'),
          },
        ],
      },
    ]
  }
  if (role === 'restaurant') {
    return [
      {
        path: '/',
        component: home,
        children: [
          {
            path: '',
            name: 'restaurant-home',
            meta: { requiresAuth: true, titleKey: 'seo.pages.home' },
            component: () => import('../views/restaurant/home_screen.vue'),
          },
          {
            path: 'orders',
            name: 'restaurant-orders',
            meta: { requiresAuth: true, titleKey: 'seo.pages.orders' },
            component: () => import('../views/restaurant/orders_screen.vue'),
          },
          {
            path: 'finance',
            name: 'restaurant-finance',
            meta: { requiresAuth: true, titleKey: 'seo.pages.finance' },
            component: () => import('../views/restaurant/finance_screen.vue'),
          },
          {
            path: 'restaurant',
            name: 'restaurant-catalog',
            meta: { requiresAuth: true, titleKey: 'seo.pages.shop' },
            component: () => import('../views/restaurant/catalog_screen.vue'),
          },
          {
            path: 'restaurant/product/new',
            name: 'restaurant-product-create',
            meta: { requiresAuth: true, titleKey: 'seo.pages.dish' },
            component: () => import('../views/restaurant/product_create_screen.vue'),
          },
          {
            path: 'restaurant/product/:id',
            name: 'restaurant-product-edit',
            meta: { requiresAuth: true, titleKey: 'seo.pages.dish' },
            component: () => import('../views/restaurant/product_edit_screen.vue'),
          },
          {
            path: 'login',
            name: 'restaurant-login',
            meta: { titleKey: 'seo.pages.login' },
            component: () => import('../views/restaurant/login_screen.vue'),
          },
          {
            path: 'register',
            name: 'restaurant-register',
            meta: { titleKey: 'seo.pages.register' },
            component: () => import('../views/restaurant/register_screen.vue'),
          },
          {
            path: 'profile',
            name: 'restaurant-profile',
            meta: { requiresAuth: true, titleKey: 'seo.pages.profile' },
            component: () => import('../views/restaurant/profile_screen.vue'),
          },
        ],
      },
    ]
  }
  return [
    {
      path: '/',
      name: 'home',
      meta: { titleKey: 'seo.pages.home' },
      component: home,
    },
  ]
}

export function routesForCurrentBuild() {
  return buildRoutesForRole(APP_ROLE)
}
